import styled from 'styled-components';
import { baseVariables, toRem } from '../../styles';

export const StyledVideoPlayer = styled.div`
  .video-image-container {
    position: relative;
  }
  img {
    max-width: -webkit-fill-available;
  }
  .video-image-btn-container {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .video-image-play-btn {
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    border-radius: 50%;
    &:focus,
    &:focus-visible {
      outline: ${toRem(2)} solid ${baseVariables.color.base10};
      outline: ${toRem(2)} solid -webkit-focus-ring-color;
    }
  }
`;

export const StyledVideoModal = styled.div`
  [class^='vjs-']::before {
    font-family: 'VideoJS';
  }
  .video-modal-container,
  .video-modal-container-mobile {
    outline: none;
    /* Prevent the modal container from being a keyboard Tab stop.
       The container has tabIndex=0 from the Modal component but is not interactive —
       keyboard users should Tab directly between the close button and player controls. */
    &:focus {
      outline: none;
    }
  }
  .video-modal-container {
    max-height: calc(100vh - 32px);
    min-height: auto;
    height: auto;
    max-width: calc(177.77vh - 64px);
    width: 80vw;
    aspect-ratio: 16 / 9;
    background-color: ${baseVariables.color.base10};
  }
  .video-modal-container-mobile {
    width: 100%;
    max-width: 100%;
    height: 100vh;
    max-height: 100vh;
    max-height: 100dvh;
    background: ${baseVariables.color['base10']};
    display: flex;
    flex-direction: column;
    justify-content: center;
    aspect-ratio: 16 / 9;
    border-radius: unset;
    margin: auto;
    min-height: -webkit-fill-available;
  }
  .video-modal-header {
    position: absolute;
    z-index: 99;
    border: none;
    padding: 0;
    height: auto;
    top: ${toRem(10)};
    right: ${toRem(10)};
  }
  .video-close-btn {
    font-size: ${toRem(24)};
    line-height: ${toRem(24)};
    &:focus,
    &:focus-visible {
      outline: ${toRem(2)} solid ${baseVariables.color.base10};
      outline: ${toRem(2)} solid -webkit-focus-ring-color;
    }
  }
  .video-modal-body {
    overflow: visible;
    padding: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    @media ${baseVariables.mediaQuery.xl} {
      display: block;
    }
  }
  .video-modal-main-container {
    @media ${baseVariables.mediaQuery.xl} {
      display: flex;
    }
    &::before {
      opacity: 0.7;
    }
    .brightcove-react-player-loader .video-js .vjs-big-play-button {
      background-color: rgba(0, 0, 0, 0.45);
      span::before {
        color: ${baseVariables.color['base20']};
      }
    }
  }
  .vjs-control-bar {
    position: fixed;
    @media ${baseVariables.mediaQuery.xl} {
      position: absolute;
    }
    .vjs-progress-control {
      height: ${toRem(4.19)};
      top: 0;
      .vjs-play-progress {
        background: ${baseVariables.color['base20']};
      }
    }
    .vjs-volume-level {
      background: ${baseVariables.color['base20']};
    }
    .vjs-menu .vjs-menu-item.vjs-selected {
      background: ${baseVariables.color['base20']};
      .vjs-menu-item-text {
        color: ${baseVariables.color['base10']};
      }
    }
  }

  /* Brightcove Video.js player — focus ring styles for all interactive controls.
     The player renders inline DOM (not an iframe), so CSS overrides apply.
     Uses -webkit-focus-ring-color to match the site-wide default focus indicator,
     consistent with Accordion, CarouselArrow, and other shared components.
     base10 is declared first as a cross-browser fallback — Firefox ignores
     -webkit-focus-ring-color and uses the preceding solid color instead.
     Video.js sets focus programmatically (menus, dialogs) so we must style
     :focus in addition to :focus-visible — programmatic focus does not
     trigger :focus-visible in most browsers. */

  /* Reset Video.js default focus styles to prevent double ring */
  .video-js button:focus,
  .video-js button:focus-visible,
  .video-js [tabindex]:focus,
  .video-js [tabindex]:focus-visible,
  .video-js select:focus,
  .video-js select:focus-visible,
  .video-js .vjs-menu-item:focus,
  .video-js .vjs-menu-item:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }

  /* Control bar buttons: play, mute, captions, quality, PiP, seek-to-live */
  .video-js .vjs-control-bar button:focus,
  .video-js .vjs-control-bar button:focus-visible,
  .video-js .vjs-control-bar .vjs-menu-button:focus,
  .video-js .vjs-control-bar .vjs-menu-button:focus-visible {
    outline: ${toRem(2)} solid ${baseVariables.color.base10} !important;
    outline: ${toRem(2)} solid -webkit-focus-ring-color !important;
    outline-offset: ${toRem(-2)};
  }

  /* Big play button overlay */
  .video-js .vjs-big-play-button:focus,
  .video-js .vjs-big-play-button:focus-visible {
    outline: ${toRem(2)} solid ${baseVariables.color.base10} !important;
    outline: ${toRem(2)} solid -webkit-focus-ring-color !important;
  }

  /* Sliders: progress bar and volume bar */
  .video-js .vjs-progress-holder:focus,
  .video-js .vjs-progress-holder:focus-visible,
  .video-js .vjs-volume-bar:focus,
  .video-js .vjs-volume-bar:focus-visible {
    outline: ${toRem(2)} solid ${baseVariables.color.base10} !important;
    outline: ${toRem(2)} solid -webkit-focus-ring-color !important;
  }

  /* Caption settings dialog: select dropdowns, Done/Reset buttons, close button */
  .video-js .vjs-text-track-settings select:focus,
  .video-js .vjs-text-track-settings select:focus-visible,
  .video-js .vjs-text-track-settings button:focus,
  .video-js .vjs-text-track-settings button:focus-visible,
  .video-js .vjs-modal-dialog .vjs-close-button:focus,
  .video-js .vjs-modal-dialog .vjs-close-button:focus-visible {
    outline: ${toRem(2)} solid ${baseVariables.color.base10} !important;
    outline: ${toRem(2)} solid -webkit-focus-ring-color !important;
  }

  /* Menu items: captions list, quality levels, descriptions */
  .video-js .vjs-menu-item:focus,
  .video-js .vjs-menu-item:focus-visible {
    outline: ${toRem(2)} solid ${baseVariables.color.base10} !important;
    outline: ${toRem(2)} solid -webkit-focus-ring-color !important;
  }

  [dir='rtl'] & {
    .video-modal-header {
      right: unset !important;
      left: ${toRem(10)} !important;
    }
    .vjs-title-bar {
      text-align: right !important;
    }
  }
`;
